import type { BrandName } from './types';

/**
 * The brand calendar document, parsed.
 *
 * docs/brand-calendar.md is the human reference: every date worth knowing
 * about, far more than the dashboard fires on. brand/calendar.json is the
 * actionable subset, and the "Coming up" panel reads that. This reads the
 * document, so the full list can be browsed without leaving the Shell and
 * without a second copy of it existing anywhere.
 *
 * Parsed in the browser rather than served by the API, deliberately. It
 * is a document being displayed, not data being computed: no lead times,
 * no timing rules, nothing another surface has to agree with. Doing it
 * here costs no endpoint, no gateway route, no edge behaviour and no
 * credential, and the file ships in the bundle it is read from.
 */

export interface CalendarDocEntry {
  /** "15 Sep", "1-31 Jan", "July, dates TBC" - as the document writes it. */
  date: string;
  /** The entry without its date and without its brand tag. */
  text: string;
  brands: BrandName[];
  /** Present when the document flags the date as unconfirmed. */
  tbc: boolean;
}

export interface CalendarDocMonth {
  /** "2026-09", for matching against today without parsing prose. */
  key: string;
  /** "September 2026", as the heading reads. */
  label: string;
  entries: CalendarDocEntry[];
}

const MONTHS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

/** ALL, CB, AF, EQ and the slash combinations the document uses. */
const BRAND_TOKENS: Record<string, BrandName[]> = {
  ALL: ['cashblack', 'afrofiliate', 'equalishop'],
  CB: ['cashblack'],
  AF: ['afrofiliate'],
  EQ: ['equalishop'],
};

function brandsOf(tag: string | undefined): BrandName[] {
  if (!tag) return [];
  const out = new Set<BrandName>();
  for (const token of tag.split('/')) {
    for (const b of BRAND_TOKENS[token.trim().toUpperCase()] ?? []) out.add(b);
  }
  return [...out];
}

/**
 * Months in document order, which is chronological.
 *
 * Only "## Month Year" headings and their "* " bullets are read. The
 * document's own preamble uses "- " for its bullets, so the explanation
 * of what the file is never arrives in the view as a date.
 */
export function parseCalendarDoc(markdown: string): CalendarDocMonth[] {
  const months: CalendarDocMonth[] = [];
  let current: CalendarDocMonth | null = null;

  for (const line of markdown.split('\n')) {
    const heading = /^##\s+([A-Za-z]+)\s+(\d{4})\s*$/.exec(line.trim());
    if (heading) {
      const monthIndex = MONTHS.indexOf(heading[1]!.toLowerCase());
      if (monthIndex === -1) {
        current = null; // a prose heading such as "## Key"
        continue;
      }
      current = {
        key: `${heading[2]}-${String(monthIndex + 1).padStart(2, '0')}`,
        label: `${heading[1]} ${heading[2]}`,
        entries: [],
      };
      months.push(current);
      continue;
    }
    if (!current || !line.startsWith('* ')) continue;

    const body = line.slice(2).trim();
    // The brand tag is the last bracketed token on the line.
    const tagged = /^(.*?)\s*\[([A-Za-z/]+)\]\s*$/.exec(body);
    const withoutTag = tagged ? tagged[1]!.trim() : body;
    // The date is everything before the first en dash. Entries without
    // one keep their whole text and show no date rather than guessing.
    const split = withoutTag.indexOf(' – ');
    const date = split === -1 ? '' : withoutTag.slice(0, split).trim();
    const text = split === -1 ? withoutTag : withoutTag.slice(split + 3).trim();

    current.entries.push({
      date,
      text,
      brands: brandsOf(tagged?.[2]),
      tbc: /\bTBC\b/i.test(withoutTag),
    });
  }

  return months;
}
